// WebAuthn utility functions for passkey registration and authentication
import { create, get } from '@github/webauthn-json';

function buildWebAuthnError(error, context) {
  if (error.name === 'NotAllowedError') {
    return new Error(`Passkey ${context} was cancelled or timed out`);
  }

  if (error.name === 'NotSupportedError') {
    return new Error('Your browser does not support passkeys');
  }

  if (context === 'registration' && error.name === 'InvalidStateError') {
    return new Error('A passkey for this account already exists');
  }

  return new Error(`Passkey ${context} failed: ${error.message}`);
}

/**
 * Register a new passkey
 * @param {Object} options - WebAuthn publicKey options from backend (not wrapped)
 * @returns {Promise<Object>} - Credential response to send to backend
 */
export async function registerPasskey(options) {
  try {
    const credentialCreationOptions = {
      publicKey: options
    };
    
    const credential = await create(credentialCreationOptions);
    
    return credential;
  } catch (error) {
    console.error('Passkey registration error:', error);
    throw buildWebAuthnError(error, 'registration');
  }
}

/**
 * Authenticate with passkey
 * @param {Object} options - WebAuthn publicKey request options from backend (not wrapped)
 * @returns {Promise<Object>} - Assertion response to send to backend
 */
export async function authenticateWithPasskey(options) {
  try {
    const credentialRequestOptions = {
      publicKey: options
    };
    
    const assertion = await get(credentialRequestOptions);
    
    return assertion;
  } catch (error) {
    console.error('Passkey authentication error:', error);
    throw buildWebAuthnError(error, 'authentication');
  }
}

export function isWebAuthnSupported() {
  return (
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function'
  );
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) {
    return false;
  }
  
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    console.error('Error checking platform authenticator:', error);
    return false;
  }
}
